import { useState, useEffect } from "react";
import { Pressable } from "react-native";
import { useUnistyles } from "react-native-unistyles";
import { View, Text } from "@src/components";
import { Icon } from "@src/components/Icon";
import { sizes } from "@src/constants/sizes";
import type { SortOption, SortState } from "@src/features/clips/types/camera";
import { CAMERA_SORT_OPTIONS } from "@src/features/cameras/utils/camera-sort-config";

interface SortDropdownProps {
  value: SortState;
  onChange: (value: SortState) => void;
}

export function SortDropdown({ value, onChange }: SortDropdownProps) {
  const { theme } = useUnistyles();
  const [isOpen, setIsOpen] = useState(false);

  const hasActiveSort = value !== null;

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  // First click on an option selects it ascending. Clicking the same option
  // again flips to descending, and a third click clears the sort entirely.
  const handleSelect = (option: SortOption) => {
    if (!value || value.option !== option) {
      onChange({ option, direction: "asc" });
      return;
    }
    if (value.direction === "asc") {
      onChange({ option, direction: "desc" });
      return;
    }
    onChange(null);
  };

  const handleClearSort = () => {
    onChange(null);
    setIsOpen(false);
  };

  // Close dropdown when clicking outside (web)
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest("[data-sort-dropdown]")) {
        setIsOpen(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [isOpen]);

  return (
    <View style={{ position: "relative" }} data-sort-dropdown>
      {/* Sort Button */}
      <Pressable
        onPress={handleToggle}
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "center",
          gap: theme.spacing.md,
          paddingHorizontal: theme.spacing["3xl"],
          paddingVertical: theme.spacing.lg,
          borderWidth: 1,
          borderColor: isOpen ? theme.colors.blue[500] : theme.colors.border,
          borderRadius: sizes.radius,
          backgroundColor: theme.colors.surface,
          height: 44,
          minWidth: 100,
          position: "relative",
        }}
      >
        <Text textStyle="sm/regular" color="text">
          Sort
        </Text>
        <Icon name="sort" size={theme.spacing["4xl"]} color={theme.colors.text} />

        {/* Badge */}
        {hasActiveSort && (
          <View
            style={{
              position: "absolute",
              top: -4,
              right: -4,
              width: 10,
              height: 10,
              borderRadius: 5,
              backgroundColor: theme.colors.blue[500],
            }}
          />
        )}
      </Pressable>

      {/* Dropdown Menu */}
      {isOpen && (
        <View
          style={{
            position: "absolute",
            top: 48,
            right: 0,
            minWidth: 160,
            backgroundColor: theme.colors.surface,
            borderRadius: sizes.radius,
            borderWidth: 1,
            borderColor: theme.colors.border,
            shadowColor: "#000",
            shadowOffset: { width: 0, height: 4 },
            shadowOpacity: 0.15,
            shadowRadius: 12,
            elevation: 8,
            zIndex: 1000,
            overflow: "hidden",
          }}
        >
          {/* Sort Options */}
          {CAMERA_SORT_OPTIONS.map((option) => {
            const isSelected = value?.option === option.value;
            const isDescending = isSelected && value?.direction === "desc";
            return (
              <Pressable
                key={option.value}
                onPress={() => handleSelect(option.value)}
                style={({ hovered }: { hovered?: boolean }) => ({
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "space-between",
                  paddingVertical: theme.spacing["3xl"],
                  paddingHorizontal: theme.spacing["4xl"],
                  backgroundColor: hovered ? theme.colors.muted : "transparent",
                })}
              >
                <Text textStyle={isSelected ? "sm/semibold" : "sm/regular"} color="text">
                  {option.label}
                </Text>
                <Icon
                  name={isDescending ? "arrowDown" : "arrowUp"}
                  size={theme.spacing["4xl"]}
                  color={isSelected ? theme.colors.blue[500] : "transparent"}
                />
              </Pressable>
            );
          })}

          {/* Divider */}
          <View
            style={{
              height: 1,
              backgroundColor: theme.colors.border,
              marginHorizontal: theme.spacing["3xl"],
            }}
          />

          {/* Clear Sort */}
          <Pressable
            onPress={handleClearSort}
            style={({ hovered }: { hovered?: boolean }) => ({
              flexDirection: "row",
              alignItems: "center",
              paddingVertical: theme.spacing["3xl"],
              paddingHorizontal: theme.spacing["4xl"],
              backgroundColor: hovered ? theme.colors.muted : "transparent",
            })}
          >
            <Text textStyle="sm/regular" color="textMuted">
              Clear sort
            </Text>
          </Pressable>
        </View>
      )}
    </View>
  );
}
